import React from 'react';
import { TransformWrapper } from 'react-zoom-pan-pinch';

import { TileContainer, TileWithDragContainer } from './Tile';
import { tileSize, boardWidth, boardHeight } from '../helpers/constants';
import { BoardWithDragLayer } from './BoardWithDragLayer';
import { BoardPosition } from '../containers/BoardPosition';
import { BoardState } from '../containers/BoardState';
import { useParams } from 'react-router-dom';
import { useGameCallbacks } from '../hooks/gameCallbacks';
import { UserId } from '../containers/UserId';
import { Console } from '../containers/Console';

type TransformChildren = {
    positionX: number,
    positionY: number,
    scale: number,
    setPositionX?: any,
};

const pan = {
    disableOnTarget: [TileContainer.styledComponentId, TileWithDragContainer.styledComponentId],
};

const wheel = {
    step: 60,
};

const options = {
    limitToBounds: false,
    minScale: 0.1,
};

const doubleClick = {
    disabled: true,
};

const defaultPositionX = (-boardWidth * tileSize) / 2 + window.innerWidth / 2 - tileSize / 2;
const defaultPositionY = (-boardHeight * tileSize) / 2 + window.innerHeight / 2 - tileSize / 2;

export const BoardTransformer = React.memo<{ readOnly?: boolean }>(({ readOnly }) => {
    const { state } = BoardState.useContainer();
    const { userId } = UserId.useContainer();

    const { board: newBoard } = state;
    const { id: roomId } = useParams();

    const lastPositionX = React.useRef(defaultPositionX);
    const lastScale = React.useRef(1);

    const { appendToLogs } = Console.useContainer();

    const {
        positionX,
        positionY,
        scale,
        setPositionX,
        setPositionY,
        setScale,
    } = BoardPosition.useContainer();

    const {
        handleDropOnBoard,
        handleClickBoardTile,
        handleDoubleClickBoardTile,
    } = useGameCallbacks({ userId, roomId });

    const handlePanning = React.useCallback(
        (params: TransformChildren) => {
            setPositionX(params.positionX);
            setPositionY(params.positionY);
        },
        [setPositionX, setPositionY],
    );

    const handleZoomChange = React.useCallback(
        (params: TransformChildren) => {
            setPositionX(params.positionX);
            setPositionY(params.positionY);
            setScale(params.scale);
        },
        [setPositionY, setPositionX, setScale],
    );

    return (
        <TransformWrapper
            doubleClick={doubleClick}
            // @ts-ignore
            pan={pan}
            wheel={wheel}
            defaultPositionX={defaultPositionX}
            defaultPositionY={defaultPositionY}
            // positionX={positionX}
            // positionY={positionY}
            // scale={scale}
            // onPanning={handlePanning}
            // onZoomChange={handleZoomChange}
            options={options}
        >
            {(params: TransformChildren) => {
                const { positionX, positionY, scale, setPositionX } = params;
                if (
                    lastScale.current === scale &&
                    Math.abs(lastPositionX.current - positionX) > 100
                ) {
                    setPositionX(lastPositionX.current, 0, 'easeOut');
                } else {
                    lastPositionX.current = positionX;
                }
                lastScale.current = scale;

                return (
                    <BoardWithDragLayer
                        positionX={positionX}
                        positionY={positionY}
                        scale={scale}
                        onDrop={handleDropOnBoard}
                        boardState={newBoard}
                        onClickTile={handleClickBoardTile}
                        onDoubleClickTile={handleDoubleClickBoardTile}
                    />
                );
            }}
        </TransformWrapper>
    );
});

